/**
 * Non-AI Sensory Strategies chat – rule-based matching from autishChat.
 * Used by the parent "I need help" overlay.
 */

const SPEECH_KEYS = [
  'wordFinding',
  'stuttering',
  'selectiveMutism',
  'nonverbal',
  'echolalia',
  'socialCommunication',
  'writingCommunication',
  'processingSpeed',
] as const;

export const KNOWLEDGE_BASE = {
  categories: {
    visual: {
      keywords: ['light', 'bright', 'fluorescent', 'screen', 'flicker', 'flash', 'glare', 'eye', 'see', 'watch', 'look', 'visual', 'color', 'pattern'],
      triggers: ['too bright', 'hurts eyes', 'flickering', 'harsh lighting', 'screen time', 'visual overload'],
      icon: '👁️',
    },
    auditory: {
      keywords: ['noise', 'sound', 'loud', 'hear', 'music', 'voice', 'talk', 'quiet', 'ring', 'beep', 'alarm', 'siren', 'crowd', 'chatter'],
      triggers: ['too loud', 'cant focus', 'overwhelming noise', 'sudden sounds'],
      icon: '👂',
    },
    tactile: {
      keywords: ['touch', 'feel', 'texture', 'clothes', 'fabric', 'tag', 'seam', 'scratch', 'itch', 'tight', 'pressure', 'skin'],
      triggers: ['uncomfortable clothing', 'scratchy', 'tags bother', 'cant stand texture'],
      icon: '✋',
    },
    vestibular: {
      keywords: ['balance', 'dizzy', 'spin', 'movement', 'motion', 'car', 'swing', 'rock', 'nausea', 'vertigo'],
      triggers: ['motion sick', 'need movement', 'feel unsteady'],
      icon: '🔄',
    },
    proprioceptive: {
      keywords: ['body', 'position', 'heavy', 'weight', 'squeeze', 'hug', 'press', 'push', 'pull', 'jump', 'muscle', 'joint'],
      triggers: ['need pressure', 'feel disconnected', 'clumsy today', 'need grounding'],
      icon: '💪',
    },
    olfactory: {
      keywords: ['smell', 'scent', 'odor', 'perfume', 'fragrance', 'nose'],
      triggers: ['strong smells', 'perfume bothers'],
      icon: '👃',
    },
    gustatory: {
      keywords: ['taste', 'food', 'eat', 'texture food', 'flavor', 'picky', 'mouth', 'chew'],
      triggers: ['picky eater', 'food texture', 'cant eat'],
      icon: '👅',
    },
    // Speech & communication
    wordFinding: {
      keywords: ['word', 'finding', 'forget word', 'tip of tongue', 'go blank', 'lose track', 'cant think', 'expressive', 'speaking', 'articulate'],
      triggers: ['cant find words', 'forget what i was saying', 'word finding difficulty', 'go blank', 'lose my train of thought'],
      icon: '🗣️',
    },
    stuttering: {
      keywords: ['stutter', 'stammer', 'repeat', 'block', 'fluency', 'cluttering'],
      triggers: ['i stutter', 'i stammer', 'speech blocks', 'fluency issues'],
      icon: '🌀',
    },
    selectiveMutism: {
      keywords: ['selective mutism', 'cant speak', 'freeze', 'silent', 'mute'],
      triggers: ['cant speak in public', 'freeze when i have to talk', 'voice disappears'],
      icon: '🤐',
    },
    nonverbal: {
      keywords: ['nonverbal', 'non-verbal', 'lose speech', 'lose words', 'aac', 'communication board'],
      triggers: ['go nonverbal', 'going nonverbal', 'lose my words', 'speech shuts down'],
      icon: '📱',
    },
    echolalia: {
      keywords: ['echolalia', 'echo', 'repeat phrases', 'scripting', 'scripts', 'scripted speech'],
      triggers: ['i repeat things', 'use scripts', 'scripted speech'],
      icon: '🔁',
    },
    socialCommunication: {
      keywords: ['conversation', 'small talk', 'topic', 'eye contact', 'interrupt', 'turn-taking', 'sarcasm', 'literal', 'figurative', 'tone'],
      triggers: ['dont understand social cues', 'cant do small talk', 'misread tone', 'take things literally'],
      icon: '💬',
    },
    writingCommunication: {
      keywords: ['write', 'writing', 'email', 'text', 'message', 'type', 'easier in writing'],
      triggers: ['easier to write', 'better in writing', 'prefer writing'],
      icon: '✍️',
    },
    processingSpeed: {
      keywords: ['slow processing', 'processing speed', 'need time', 'takes time', 'pressure to respond', 'auditory processing'],
      triggers: ['slow to process', 'need time to respond', 'pressure to answer quickly'],
      icon: '⏱️',
    },
  },
  situations: {
    overwhelm: { keywords: ['overwhelm', 'too much', 'cant cope', 'shutdown', 'meltdown', 'panic', 'stress', 'anxiety', 'overload', 'overstimulated'], icon: '😰' },
    focus: { keywords: ['focus', 'concentrate', 'attention', 'distract', 'work', 'study', 'task', 'homework'], icon: '🎯' },
    social: { keywords: ['social', 'people', 'crowd', 'party', 'gathering', 'conversation', 'group', 'public'], icon: '👥' },
    sleep: { keywords: ['sleep', 'bed', 'night', 'rest', 'insomnia', 'wake', 'tired', 'relax', 'wind down'], icon: '😴' },
    transition: { keywords: ['change', 'transition', 'switch', 'new', 'different', 'routine', 'schedule', 'unexpected'], icon: '🔀' },
    regulation: { keywords: ['regulate', 'calm', 'soothe', 'ground', 'reset', 'recover', 'decompress', 'recharge', 'break'], icon: '⚖️' },
  },
  phrasePatterns: [
    { pattern: /can'?t (stand|handle|deal with|cope)/i, boost: ['overwhelm'] },
    { pattern: /too (much|many|loud|bright|strong)/i, boost: ['overwhelm'] },
    { pattern: /need(s|ing)? (help|to calm|to focus|a break)/i, boost: ['regulation'] },
    { pattern: /sensory (diet|break|input)/i, boost: ['regulation', 'proprioceptive'] },
    { pattern: /meltdown|shutdown/i, boost: ['overwhelm'] },
    { pattern: /noise cancel/i, boost: ['auditory'] },
    { pattern: /can'?t sleep|insomnia/i, boost: ['sleep'] },
    { pattern: /social|party|gathering|crowd/i, boost: ['social', 'auditory', 'overwhelm'] },
    // Speech-specific patterns
    { pattern: /word[- ]finding|blank on words|cant find words/i, boost: ['wordFinding'] },
    { pattern: /stutter|stammer|fluency/i, boost: ['stuttering'] },
    { pattern: /selective mutism|freeze when i talk|cant speak in (public|groups?)/i, boost: ['selectiveMutism'] },
    { pattern: /go nonverbal|non[- ]verbal|lose my words/i, boost: ['nonverbal'] },
    { pattern: /echo(lalia)?|script(ing)?|repeat (phrases|what)/i, boost: ['echolalia'] },
    { pattern: /small talk|social cues|tone of voice|sarcasm/i, boost: ['socialCommunication'] },
    { pattern: /easier to write|prefer writing|cant say it but can type/i, boost: ['writingCommunication'] },
    { pattern: /slow processing|need time to respond|pressure to answer/i, boost: ['processingSpeed'] },
  ],
} as const;

const STRATEGIES: Record<string, { immediate?: string[]; tools?: string[]; environmental?: string[]; longterm?: string[] }> = {
  visual: {
    immediate: ['Close your eyes or look down for 30 seconds', 'Cup your hands over your eyes (palming)', 'Focus on a single, still object', 'Turn away from the brightest light source'],
    tools: ['Sunglasses or tinted lenses', 'Baseball cap or wide-brimmed hat', 'Blue light blocking glasses for screens'],
    environmental: ['Replace fluorescent lights with softer options', 'Use natural light or lamps', 'Create a low-light corner for decompression'],
    longterm: ['Ask for accommodations at school/work', 'Build in regular visual breaks'],
  },
  auditory: {
    immediate: ['Cover your ears or press your palms against them briefly', 'Hum or make a steady sound to mask noises', 'Move to a quieter area (bathroom, hallway, outside)'],
    tools: ['Noise-canceling headphones or earbuds', 'Loop or Calmer earplugs', 'White noise machine or app'],
    environmental: ['Identify quiet spaces in frequently visited places', 'Sit away from speakers or high-traffic areas'],
    longterm: ['Build noise tolerance gradually', 'Establish a signal with trusted people when you need quiet'],
  },
  tactile: {
    immediate: ['Remove or adjust the offending item if possible', 'Apply firm pressure to the area', 'Splash cold water on hands or face', 'Focus on a texture you DO like'],
    tools: ['Seamless socks and tagless clothing', 'Compression clothing or vest', 'Fidget toys with preferred textures', 'Weighted blanket or lap pad'],
    environmental: ['Wash new clothes multiple times before wearing', 'Create a sensory-friendly wardrobe'],
    longterm: ['Build a tactile tolerance routine', 'Work with an OT on desensitization if helpful'],
  },
  proprioceptive: {
    immediate: ['Wall push-ups or press palms hard against a wall', 'Give yourself a tight self-hug', 'Push down hard on chair arms', 'Do 10 jumping jacks or march in place'],
    tools: ['Weighted blanket (typically 10% of body weight)', 'Compression vest', 'Chew jewelry or gum', 'Resistance bands'],
    environmental: ['Set up a crash pad or pile of pillows', 'Keep heavy items to carry', 'Use a therapy ball as a chair'],
    longterm: ['Build regular heavy work into your routine', 'Consider swimming, climbing, or martial arts'],
  },
  vestibular: {
    immediate: ['Plant your feet firmly and focus on that connection', 'Hold onto something stable', 'Rock gently side to side in a rhythm you control'],
    tools: ['Wobble cushion or balance board', 'Rocking chair or glider', 'Swing (indoor or outdoor)'],
    environmental: ['Choose seats where you can see movement coming', 'Avoid middle seats in cars if motion sensitive'],
    longterm: ['Build vestibular tolerance gradually', 'Work with an OT on vestibular integration'],
  },
  olfactory: {
    immediate: ['Breathe through your mouth temporarily', 'Smell something you DO like to override', 'Move away from the source', 'Cover your nose with your shirt'],
    tools: ['Personal aromatherapy (essential oil on wrist)', 'Small vial of a preferred scent', 'Unscented products for your own use'],
    environmental: ['Use air purifiers at home', 'Request fragrance-free policies at work/school'],
    longterm: ['Document your smell triggers', 'Communicate your needs to close contacts'],
  },
  gustatory: {
    immediate: ['Have a safe food or drink available to reset your palate', 'Chew gum or eat a mint', 'Drink water to neutralize'],
    tools: ['Safe food kit for carrying preferred items', 'Smoothies/purees to get nutrition without texture'],
    environmental: ['Prepare safe meals ahead of time', 'Research restaurant menus before going out'],
    longterm: ['Work with a feeding therapist if nutrition is impacted', 'Build food variety gradually from trusted base foods'],
  },
  overwhelm: {
    immediate: ['Remove yourself from the situation—any exit is okay', 'Reduce input: eyes closed, ears covered', '5-4-3-2-1 technique: 5 things you see, 4 hear, 3 touch, 2 smell, 1 taste', 'Stimming is regulating—rock, flap, hum, whatever helps'],
    tools: ['Emergency sensory kit: sunglasses, earplugs, fidget, safe snack', 'Pre-written cards to show others when you cant speak', 'Phone with calming app ready'],
    environmental: ['Know your exits—always identify escape routes', 'Have a safe person who can help you leave'],
    longterm: ['Learn your early warning signs of overload', 'Build in prophylactic breaks before overwhelm'],
  },
  focus: {
    immediate: ['Body double: have someone else present (even virtually)', 'Set a timer for a short burst (15-25 minutes)', 'Change your position: stand, sit on floor, pace while thinking'],
    tools: ['Noise-canceling headphones with brown noise or lo-fi', 'Fidget that doesnt require visual attention', 'Standing desk or desk cycle'],
    environmental: ['Face a blank wall or window (not a busy area)', 'Have only current task materials visible'],
    longterm: ['Identify your peak focus times and protect them', 'Break tasks into smaller, concrete steps'],
  },
  social: {
    immediate: ['Take bathroom breaks as sensory breaks', 'Find the quietest corner or edge of the room', 'Have a task (helping, holding something) for body and attention'],
    tools: ['Discreet earplugs (Loop, Vibes)', 'Sunglasses if lighting is harsh', 'Phone as legitimate break'],
    environmental: ['Arrive early before crowds build', 'Know the layout and identify quiet zones', 'Have an exit time agreed on in advance'],
    longterm: ['Build social stamina gradually', 'Schedule recovery time after social events'],
  },
  sleep: {
    immediate: ['Deep pressure: weighted blanket, tight sheets, body pillow', 'White noise or fan for consistent sound', 'Complete darkness or sleep mask', 'Cool room temperature (65-68°F)'],
    tools: ['Blackout curtains or sleep mask', 'White noise machine or app', 'Weighted blanket', 'Comfortable, tagless pajamas'],
    environmental: ['Make bedroom for sleep only', 'Remove visible screens and lights'],
    longterm: ['Consistent sleep schedule', 'Wind-down routine at least 30 min before bed'],
  },
  regulation: {
    immediate: ['Deep breaths: in for 4, hold for 4, out for 6', 'Cold water on wrists and face', 'Heavy work: push against wall, squeeze stress ball', 'Rhythmic movement: rock, swing, pace'],
    tools: ['Weighted items (blanket, lap pad)', 'Chewable jewelry or gum', 'Aromatherapy (lavender for calm)'],
    environmental: ['Create a dedicated calm-down space', 'Low lighting and reduced visual clutter'],
    longterm: ['Develop a personalized sensory diet', 'Learn your body\'s early signals of dysregulation'],
  },
  transition: {
    immediate: ['Use a visual timer or countdown', 'Give a 5- and 2-minute warning', 'Carry a transition object from one activity to the next', 'Name the next step out loud'],
    tools: ['Visual schedule or checklist', 'Timer (visual or auditory)', 'Transition card or token'],
    environmental: ['Keep routines predictable when possible', 'Build in buffer time between activities'],
    longterm: ['Create consistent transition routines', 'Practice transitions in low-stakes settings'],
  },
  // Speech & communication strategies – trimmed but representative
  wordFinding: {
    immediate: [
      'Pause and describe the idea around the word instead of forcing the exact term',
      'Use a gesture, synonym, or example while the word comes back',
    ],
    tools: [
      'Keep a running personal glossary in your notes app for tricky words and phrases',
      'Use visual mind-maps or keyword lists when speaking in meetings or calls',
    ],
    environmental: ['Let trusted people know you may pause or circle around a word while you find it'],
    longterm: ['Practice rehearsing key phrases before stressful situations like presentations or phone calls'],
  },
  stuttering: {
    immediate: [
      'Slow your breathing: in through your nose, out through your mouth, before starting a sentence',
      'Gently stretch the first sound of the word instead of forcing it through a block',
    ],
    tools: [
      'Use pacing apps or metronome beats to support slower, rhythmic speech practice',
      'Record short voice notes in a safe context to experiment with easier speaking patterns',
    ],
    environmental: ['Ask close people to give you time to finish without jumping in or guessing words'],
    longterm: ['Consider working with a speech therapist who specializes in neurodiversity-affirming fluency support'],
  },
  selectiveMutism: {
    immediate: [
      'Give yourself permission not to speak right now; focus on breathing and body safety first',
      'Use an alternative channel (typing on your phone, pointing, or showing a prepared card) to communicate essentials',
    ],
    tools: [
      'Prepare small written cards that explain “I find it hard to speak in some situations; typing works better for me”',
      'Use messaging, email, or chat apps for situations that reliably trigger speech freeze where possible',
    ],
    environmental: ['Arrange with trusted people that they will not pressure you to speak when you freeze'],
    longterm: ['Build speaking attempts very gradually in low-pressure settings with support from a therapist if available'],
  },
  nonverbal: {
    immediate: [
      'Switch to AAC, typing, or gestures as soon as you notice words dropping out',
      'Reduce sensory load (sound, light, people) so your energy can go to communication',
    ],
    tools: [
      'Keep a simple AAC board or app with core phrases you need when speech goes offline',
      'Store a few “I am nonverbal right now” messages in your phone that you can show people',
    ],
    environmental: ['Let key people know that going nonverbal is a regulation strategy, not rudeness'],
    longterm: ['Develop and practice non-speech communication methods you like before you need them in crisis'],
  },
  echolalia: {
    immediate: [
      'Use the script that feels safest and, if you can, pair it with a gesture that shows what you mean',
      'Give yourself permission to script as a way to regulate in low-stakes moments',
    ],
    tools: [
      'Create a phrase bank of your favourite scripts and what they usually mean for you',
      'Use an AAC app or note on your phone to translate scripts into plain language if needed',
    ],
    environmental: ['Teach close people what certain repeated phrases tend to mean for you'],
    longterm: ['Expand your phrase bank over time instead of trying to eliminate scripting entirely'],
  },
  socialCommunication: {
    immediate: [
      'Prepare one or two safe small-talk openers you can reuse instead of improvising every time',
      'Give yourself permission to leave or take a break from conversations that are too intense',
    ],
    tools: [
      'Write short scripts for common situations (introductions, phone calls, ordering, meetings)',
      'Use text or chat to follow up if you feel you mis-spoke or were misunderstood',
    ],
    environmental: ['Choose quieter, smaller settings for important conversations whenever possible'],
    longterm: ['Practice social scripts with a trusted person and adapt them to feel more like your own voice'],
  },
  writingCommunication: {
    immediate: [
      'If speaking feels impossible, switch to writing or typing without apologizing for it',
      'Ask “Can I put this in writing?” when you feel stuck mid-conversation',
    ],
    tools: [
      'Use templates for frequent emails or messages so you are not starting from a blank page each time',
      'Keep a shared doc or note for recurring phrases you use with school, work, or providers',
    ],
    environmental: ['Where you can, choose roles and tasks that value written communication'],
    longterm: ['Normalize for yourself and others that writing is a valid primary communication mode'],
  },
  processingSpeed: {
    immediate: [
      'Buy yourself time by saying “Give me a second to think about that” before you answer',
      'Focus on one question at a time; write it down if multiple things are being asked at once',
    ],
    tools: [
      'Use meeting notes or shared documents so you can respond in writing after you have processed',
      'Agree on signals with close people that mean “please slow down” or “one thing at a time”',
    ],
    environmental: ['Ask for agendas in advance for meetings or appointments when possible'],
    longterm: ['Practice advocating for “processing time” as a standard part of how you communicate'],
  },
};

const TITLES: Record<string, string> = {
  visual: 'Visual Strategies',
  auditory: 'Sound & Noise',
  tactile: 'Touch & Texture',
  proprioceptive: 'Body Awareness & Pressure',
  vestibular: 'Movement & Balance',
  olfactory: 'Smell Strategies',
  gustatory: 'Taste & Food',
  overwhelm: 'Managing Overwhelm',
  focus: 'Focus Strategies',
  social: 'Social Situations',
  sleep: 'Sleep Support',
  transition: 'Handling Changes',
  regulation: 'Self-Regulation',
  wordFinding: 'Word Finding',
  stuttering: 'Fluency & Stuttering',
  selectiveMutism: 'Selective Mutism',
  nonverbal: 'Nonverbal & AAC',
  echolalia: 'Echolalia & Scripting',
  socialCommunication: 'Social Communication',
  writingCommunication: 'Written Communication',
  processingSpeed: 'Processing Speed',
};

// Lightweight session context so we don't repeat the same strategy lines over and over.
const MAX_TURNS = 20;

const session = {
  seenStrategies: new Set<string>(),
  lastTopics: [] as string[],
  turns: 0,
  topicCounts: new Map<string, number>(),
  reset() {
    this.seenStrategies.clear();
    this.lastTopics = [];
    this.turns = 0;
    this.topicCounts.clear();
  },
  recordTurn(topics: string[]): { shouldFlush: boolean } {
    this.turns += 1;
    this.lastTopics = topics;
    topics.forEach((t) => this.topicCounts.set(t, (this.topicCounts.get(t) ?? 0) + 1));
    return { shouldFlush: this.turns >= MAX_TURNS };
  },
  topicCount(key: string): number {
    return this.topicCounts.get(key) ?? 0;
  },
  seen(text: string): boolean {
    return this.seenStrategies.has(text);
  },
  markSeen(text: string): void {
    this.seenStrategies.add(text);
  },
};

export function resetStrategySession() {
  session.reset();
}

export function getStrategySessionInfo() {
  return {
    turns: session.turns,
    maxTurns: MAX_TURNS,
    lastTopics: [...session.lastTopics],
  };
}

export function analyzeInput(userText: string): { topMatches: string[]; hasMatches: boolean } {
  const text = userText.toLowerCase();
  const scores: Record<string, number> = {};

  Object.keys(KNOWLEDGE_BASE.categories).forEach((cat) => { scores[cat] = 0; });
  Object.keys(KNOWLEDGE_BASE.situations).forEach((sit) => { scores[sit] = 0; });

  Object.entries(KNOWLEDGE_BASE.categories).forEach(([category, data]) => {
    data.keywords.forEach((keyword) => {
      if (text.includes(keyword)) scores[category] += 2;
    });
    data.triggers.forEach((trigger) => {
      if (text.includes(trigger)) scores[category] += 5;
    });
  });

  Object.entries(KNOWLEDGE_BASE.situations).forEach(([situation, data]) => {
    data.keywords.forEach((keyword) => {
      if (text.includes(keyword)) scores[situation] += 2;
    });
  });

  KNOWLEDGE_BASE.phrasePatterns.forEach(({ pattern, boost }) => {
    if (pattern.test(text)) {
      boost.forEach((key) => {
        if (scores[key] !== undefined) scores[key] += 3;
      });
    }
  });

  // Small carry-forward boost for last topics to keep short conversational context.
  session.lastTopics.forEach((key) => {
    if (scores[key] !== undefined) scores[key] += 1;
  });

  const sorted = Object.entries(scores)
    .filter(([, score]) => score > 0)
    .sort((a, b) => b[1] - a[1]);

  return {
    topMatches: sorted.slice(0, 3).map(([key]) => key),
    hasMatches: sorted.length > 0,
  };
}

function pickStrategies(
  strategySet: { immediate?: string[]; tools?: string[]; environmental?: string[]; longterm?: string[] },
  perResponseUsed: Set<string>,
  revisitDepth: number
): { type: string; text: string }[] {
  const out: { type: string; text: string }[] = [];

  const tiers =
    revisitDepth <= 1
      ? [
          ['immediate', 'Right now'],
          ['tools', 'Tool'],
          ['environmental', 'Environment'],
        ]
      : revisitDepth === 2
      ? [
          ['tools', 'Tool'],
          ['environmental', 'Environment'],
          ['longterm', 'Longer term'],
        ]
      : [
          ['longterm', 'Longer term'],
          ['environmental', 'Environment'],
          ['immediate', 'Right now'],
        ];

  for (const [tier, label] of tiers) {
    const pool = (strategySet as any)[tier] as string[] | undefined;
    if (!pool) continue;
    const available = pool.filter((s) => !session.seen(s) && !perResponseUsed.has(s));
    if (available.length) {
      const choice = available[Math.floor(Math.random() * available.length)];
      out.push({ type: label, text: choice });
      session.markSeen(choice);
      perResponseUsed.add(choice);
      if (out.length >= 3) break;
    }
  }

  if (out.length === 0) {
    const all = ['immediate', 'tools', 'environmental', 'longterm']
      .flatMap((tier) => ((strategySet as any)[tier] as string[] | undefined) ?? [])
      .filter((s) => !perResponseUsed.has(s));
    if (all.length) {
      const choice = all[Math.floor(Math.random() * all.length)];
      out.push({ type: 'Reminder', text: choice });
      perResponseUsed.add(choice);
    }
  }

  return out;
}

function formatTitle(key: string): string {
  return TITLES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function generateResponse(userText: string): string {
  const trimmed = userText.trim();
  // Manual reset command to flush context if needed.
  if (/^reset$/i.test(trimmed)) {
    session.reset();
    return "<p>Session cleared. Fresh start — what's going on right now?</p>";
  }

  const analysis = analyzeInput(trimmed);

  if (!analysis.hasMatches) {
    return [
      "Could you tell me more about what you're experiencing? For example:",
      "<ul>",
      "<li>What sensory input is bothering you? (sounds, lights, textures, smells)</li>",
      "<li>Are you having difficulty speaking, finding words, or communicating?</li>",
      "<li>What situation are you in? (work, school, social event, home)</li>",
      "<li>What do you need help with? (calming down, focusing, coping with a specific trigger)</li>",
      "</ul>",
      "<p>The more specific you can be, the better I can match you with helpful strategies.</p>",
    ].join('');
  }

  const sections: { key: string; title: string; strategies: { type: string; text: string }[] }[] = [];
  const perResponseUsed = new Set<string>();

  const { shouldFlush } = session.recordTurn(analysis.topMatches);

  analysis.topMatches.forEach((match) => {
    const strategySet = STRATEGIES[match];
    if (!strategySet) return;
    const icon =
      (KNOWLEDGE_BASE.categories as Record<string, { icon?: string }>)[match]?.icon ??
      (KNOWLEDGE_BASE.situations as Record<string, { icon?: string }>)[match]?.icon ??
      '💡';
    const revisitDepth = session.topicCount(match);
    const selected = pickStrategies(strategySet, perResponseUsed, revisitDepth);
    if (selected.length) {
      sections.push({ key: match, title: `${icon} ${formatTitle(match)}`, strategies: selected });
    }
  });

  if (sections.length === 0) {
    return "<p>I'm not sure I have strategies for that. Try describing the sensory challenge or situation in different words. If this still doesn't feel helpful, you can use <strong>Contact a therapist</strong> below to connect with a professional.</p>";
  }

  let html =
    '<p>Based on what you&apos;ve described, here are some sensory and communication strategies that might help:</p>';
  sections.forEach((section) => {
    html += `<div class="strategy-section"><h4>${section.title}</h4><ul>`;
    section.strategies.forEach((s) => {
      html += `<li><span class="category-tag">${s.type}</span>${s.text}</li>`;
    });
    html += '</ul></div>';
  });
  html +=
    '<p style="margin-top: 15px; font-size: 0.9rem;">If these ideas aren\'t enough, you can ask to <strong>connect with a therapist</strong>. Your organization may route this to an assigned therapist, or we can help you use <strong>Find a therapist</strong>.</p>';
  return html;
}

export const QUICK_PROMPTS = [
  { label: 'Bright lights', prompt: 'The lights are too bright and hurting my eyes' },
  { label: 'Too much noise', prompt: "There's too much noise and I can't focus" },
  { label: 'Clothing discomfort', prompt: 'My clothes feel uncomfortable and scratchy' },
  { label: 'Crowded spaces', prompt: "I'm feeling overwhelmed in a crowded place" },
  { label: 'Need to calm down', prompt: 'I need to calm down and regulate' },
  { label: 'Word finding is hard', prompt: "I can't find words in conversations and my brain goes blank" },
  { label: 'I go nonverbal', prompt: 'When I get overwhelmed my speech shuts down and I go nonverbal' },
];
